import React, { useEffect, useState } from "react";
import axios from "axios";
import moment from "moment";
import { toast } from "react-toastify";
import { useNavigate } from "react-router";
import { useSelector } from "react-redux";

import { KioskStorage } from "./component/styled/storage";
import { Container } from "./component/styled/common";

import KioskHeader from "./component/common/kioskHeader";
import KioskFooter from "./component/common/kioskFooter";
import TabMenu from "./component/kioskStorage/tabMenu";
import ViewDetails from "./component/kioskStorage/viewDetails";
import ViewAll from "./component/kioskStorage/viewAll";
import ViewType from "./component/kioskStorage/viewType";

import UserInfoModal from "./component/kioskStorage/modal/userInfoModal";
import UserInfoForm from "./component/kioskStorage/modal/userInfoForm";
import CustResvModal from "./component/kioskStorage/modal/custResvModal";
import StoreSettings from "./component/kioskStorage/modal/storeSettings";

import { printLabel, storageType } from "./component/function/kiosk";
import { useRegex } from "../../../hooks/useRegex";
import { useDisableScroll } from "../../../hooks/useDisableScroll";
import { checkKioskCond } from "../dashboard/component/function/dashboard";

const KioskStoragePage = () => {
  useDisableScroll();

  const navigate = useNavigate();

  const [type, setType] = useState(0);
  const [active, setActive] = useState(0);
  const [selected, setSelected] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [data, setData] = useState([]);
  const [origin, setOrigin] = useState([]);
  const [modalType, setModalType] = useState("");

  const { TelFormat } = useRegex();

  const user = useSelector((state) => state?.user?.data[0]);
  const company = useSelector((state) => state?.company?.data);

  useEffect(() => {
    if (!checkKioskCond(company)) {
      toast.error("접근 권한이 없습니다.", { toastId: "no-access" });
      navigate("/mypage");
      return;
    }
  }, []);

  useEffect(() => {
    getEnableStorage();
  }, [active]);

  useEffect(() => {
    setData(makeStorage(origin));
  }, [origin]);

  const getEnableStorage = async () => {
    const url = "/api/enable-storage";
    const body = { type: active, point: "sungsim" };
    const res = await axios.post(url, body);
    if (res.status === 200) {
      setOrigin(res.data);
    }
  };

  const makeStorage = (specificData) => {
    const dataLength = 500;
    const config = {
      0: { baseIndex: 1 },
      1: { baseIndex: 501 },
      2: { baseIndex: 2001 },
    };

    const { baseIndex } = config[active] || { baseIndex: 1 };

    const specificDataMap = specificData.reduce((acc, item) => {
      acc[item.storageId] = item;
      return acc;
    }, {});

    return Array.from({ length: dataLength }, (_, idx) => {
      const index = baseIndex + idx;

      const idxs = [167, 179, 180, 181, 193, 194, 195, 207, 208, 209];
      const isLocked =
        (index >= 351 && index <= 500) ||
        (index >= 761 && index <= 1000) ||
        (index >= 2101 && index <= 2500);
      const isPaused = idxs.includes(index);

      let status = "open";

      if (isLocked) {
        status = "locked";
      } else if (isPaused) {
        status = "paused";
      }

      return {
        ...specificDataMap[index],
        index,
        status,
      };
    });
  };

  const handleClickStorage = (type, item = null) => {
    if (item) {
      setSelected({ ...item, quantity: calcItemCount(item) });
    }
    setModalType(type);
    setOpenModal(true);
  };

  const handleExitModal = async (flag = false) => {
    if (flag) {
      await getEnableStorage();
    }
    setSelected(null);
    setModalType("");
    setOpenModal(false);
  };

  const calcItemCount = (item) => {
    return data.filter((user) => user.mberNm === item.mberNm && user.tel === item.tel).length || 0;
  };

  const getModalContent = () => {
    switch (modalType) {
      case "userInfoForm":
        return (
          <UserInfoForm
            handleExitModal={handleExitModal}
            active={active}
            getPrintElement={getPrintElement}
          />
        );
      case "custResv":
        return (
          <CustResvModal handleExitModal={handleExitModal} getPrintElement={getPrintElement} />
        );
      case "userInfo":
        return <UserInfoModal item={selected} handleExitModal={handleExitModal} />;
      case "storeSettings":
        return <StoreSettings handleExitModal={handleExitModal} />;
      default:
        return null;
    }
  };

  const maskMiddle = (formatted) => {
    return formatted.replace(/(\d{3})-(\d)(\d{3})-(\d{4})/, "$1-$2***-$4");
  };

  // WebPrint SDK 출력
  const getPrintElement = async (items) => {
    for (const item of items) {
      const result = {
        mberNm: item.mberNm,
        storageId: item.storageId,
        type: storageType[item.type].label,
        tel: maskMiddle(TelFormat(item.tel)),
        reserveId: item.reserveId,
        reservationTime: item.reservationStartTime,
      };

      try {
        await printLabel(result);
      } catch (err) {
        toast.error("직원용 라벨 출력 중 에러 발생");
        console.error("sdk printer", err);
        return;
      }
    }

    const customerData = {
      mberNm: items[0].mberNm,
      reserveId: items[0].reserveId,
      reservationTime: items[0].reservationStartTime,
      type: items.reduce((acc, item) => {
        acc[item.type] = acc[item.type] || [];
        acc[item.type].push(item.storageId);
        return acc;
      }, {}),
    };

    try {
      await new Promise((res) => setTimeout(res, 500));
      await printLabel(customerData, true);
    } catch (err) {
      toast.error("고객용 라벨 출력 중 에러 발생");
      console.error("sdk printer", err);
    }
  };

  return (
    <Container>
      <KioskStorage className="sungsim">
        <KioskHeader title={"보관"} handleClickStorage={handleClickStorage} />
        <TabMenu active={active} setActive={setActive} />
        <ViewType
          type={type}
          setType={setType}
          active={active}
          handleClickStorage={handleClickStorage}
        />
        {type ? (
          <ViewDetails data={data} handleClickStorage={handleClickStorage} active={active} />
        ) : (
          <ViewAll data={data} handleClickStorage={handleClickStorage} active={active} />
        )}
        <KioskFooter />
        {openModal && getModalContent()}
      </KioskStorage>
    </Container>
  );
};

export default KioskStoragePage;
